import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getPatients,
  getPatient,
  getPatientVisits,
} from '@/features/patients/apis/patientsApi'
import { getActions } from '@/features/patient-actions/apis/patientActionsApi'
import {
  Patient,
  PatientVisit,
  PaginatedPatientsResponse,
  PatientSearchParams,
} from '@/features/patients/types/patients'
import { Action } from '@/features/patient-actions/types/patientActions'

// ========================================
// usePatientsQuery - 患者一覧取得
// ========================================

interface UsePatientsQueryOptions {
  params?: PatientSearchParams
  enabled?: boolean
}

/**
 * 患者一覧取得クエリ（ページネーション対応）
 *
 * @param options.params - 検索パラメータ
 * @param options.enabled - クエリを有効にするか
 * @returns TanStack Query result with data: PaginatedPatientsResponse
 */
export const usePatientsQuery = (options: UsePatientsQueryOptions = {}) => {
  const { params, enabled = true } = options

  // params を queryKey に含める（@tanstack/query/exhaustive-deps 対応）
  // 型キャストではなく直接渡すことで ESLint が正しく依存を認識できる
  return useQuery({
    queryKey: [...queryKeys.patientManagement.patients(params), params],
    queryFn: async (): Promise<PaginatedPatientsResponse> => {
      return await getPatients(params)
    },
    enabled,
    staleTime: 1000 * 60 * 2, // 2分キャッシュ
  })
}

// ========================================
// usePatientDetailQuery - 患者詳細取得
// ========================================

/**
 * 患者詳細取得クエリ
 *
 * @param patientId - 患者ID（null の場合はスキップ）
 * @returns TanStack Query result with data: Patient | null
 */
export const usePatientDetailQuery = (patientId: string | null) => {
  return useQuery({
    queryKey: queryKeys.patientManagement.patientDetail(patientId ?? ''),
    queryFn: async (): Promise<Patient | null> => {
      if (!patientId) {
        return null
      }
      return await getPatient(patientId)
    },
    enabled: !!patientId,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })
}

// ========================================
// usePatientDetailsQuery - 患者詳細（アクション+来院履歴）
// ========================================

interface PatientDetailsData {
  actions: Action[]
  visits: PatientVisit[]
}

/**
 * 患者のアクション・来院履歴を統合して取得するクエリ
 *
 * @param patientId - 患者ID（null の場合はスキップ）
 * @returns TanStack Query result with data: PatientDetailsData & actionsByStatus
 */
export const usePatientDetailsQuery = (patientId: string | null) => {
  const query = useQuery({
    queryKey: queryKeys.patientManagement.patientDetails(patientId ?? ''),
    queryFn: async (): Promise<PatientDetailsData> => {
      if (!patientId) {
        return { actions: [], visits: [] }
      }

      // 並行してデータを取得
      const [actionsResponse, visitsResponse] = await Promise.all([
        getActions({ patient: patientId }),
        getPatientVisits(patientId),
      ])

      return {
        actions: actionsResponse.results || [],
        visits: visitsResponse.results || [],
      }
    },
    enabled: !!patientId,
    staleTime: 1000 * 60 * 2, // 2分キャッシュ
  })

  // ステータス別にアクションをグループ化（派生データ）
  const actionsByStatus = useMemo(() => {
    const actions = query.data?.actions || []
    return {
      pending: actions.filter((a) => a.status === 'pending'),
      planned: actions.filter((a) => a.status === 'planned'),
      inProgress: actions.filter((a) => a.status === 'in_progress'),
      declined: actions.filter((a) => a.status === 'declined'),
      done: actions.filter((a) => a.status === 'done'),
    }
  }, [query.data?.actions])

  return {
    actionsByStatus,
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
  }
}
